import React, { useState, useEffect } from "react";
import { endPoints } from "../../constant/Environment";
import { addData, getAllData } from "../../Utility/API";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";

// CASH pays into the withdrawable balance, BONUS into the bonus balance.
const REWARD_TYPES = ["CASH", "BONUS", "BOTH", "BONUS_PERCENT", "REFERRAL_BOOST", "FREE_SPINS"];

// The same providers and spin counts as the admin Grant Free Round form.
const FREE_SPIN_PROVIDERS = [
  { value: "netgaming", label: "NetGaming" },
  { value: "blazegaming", label: "BlazeGaming" },
  { value: "kagaming", label: "KA Gaming" },
  { value: "evoplay", label: "Evoplay" },
];
const FIXED_SPIN_COUNTS = [5, 10, 50, 100];
const hasFixedSpinCounts = (provider) => provider === "kagaming" || provider === "evoplay";

// The API returns dates as ISO-8601 carrying the server's offset. Convert to the
// "yyyy-MM-ddTHH:mm" a datetime-local input needs, in the admin's own timezone.
const toPickerValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value).substring(0, 16);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Picker value -> absolute instant, so the promo window opens and closes at the
// moment the admin picked no matter which timezone anyone is in.
const toInstant = (value) => (value ? new Date(value).toISOString() : null);

const givesCash = (type) => ["CASH", "BOTH", "WELCOME", "REFERRAL"].includes(type);
const givesBonus = (type) => ["BONUS", "BOTH", "WELCOME", "REFERRAL"].includes(type);
const isBonusPercent = (type) => type === "BONUS_PERCENT";
// REFERRAL_BOOST reuses bonusPercentage, but applies to the next referral bonus
// rather than to a package purchase — so no package picker.
const isReferralBoost = (type) => type === "REFERRAL_BOOST";
const isFreeSpins = (type) => type === "FREE_SPINS";

const emptyForm = {
  promoCode: "",
  title: "",
  description: "",
  rewardType: "BONUS",
  cashRewardUsd: "",
  bonusRewardUsd: "",
  bonusPercentage: "",
  // Purchase gate: the player must have bought at least minPurchaseAmount
  // (USD) in the last purchasePeriodDays days before the code will pay out.
  minPurchaseAmount: "",
  purchasePeriodDays: 3,
  freeSpinProvider: "kagaming",
  freeSpinGameId: "",
  freeSpinGameName: "",
  freeSpinCount: "",
  freeSpinBetUsd: "",
  freeSpinCoinLevel: "",
  freeSpinCampaignId: "",
  freeSpinExpiryDays: 7,
  startDate: "",
  endDate: "",
  perUserLimit: 1,
  totalLimit: 0,
  active: true,
};

const CreatePromoCampaign = ({ open, onClose, editData = {} }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [packages, setPackages] = useState([]);
  const [bonusPackage, setBonusPackage] = useState(null); // single selected package
  const [spinGames, setSpinGames] = useState([]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  // Load the games for the selected free spin provider, as the Grant Free Round form does.
  useEffect(() => {
    if (!open || !isFreeSpins(form.rewardType) || !form.freeSpinProvider) return;
    let stale = false;
    setSpinGames([]);
    getAllData(`${endPoints.api.FREE_GAMES_BY_PROVIDER}${encodeURIComponent(form.freeSpinProvider)}`)
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || [];
        if (!stale) setSpinGames(Array.isArray(list) ? list : []);
      })
      .catch((err) => console.error(err));
    return () => {
      stale = true;
    };
  }, [open, form.rewardType, form.freeSpinProvider]);

  // Load coin packages once (for the BONUS_PERCENT package picker). /admin/packages
  // returns a raw array, so accept either that or a {data:[...]} wrapper.
  useEffect(() => {
    if (!open) return;
    getAllData(`${endPoints.api.GET_PACKAGE}`)
      .then((res) => setPackages(Array.isArray(res) ? res : res?.data || []))
      .catch((err) => console.error(err));
  }, [open]);

  // Fill the form from editData (or reset) each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    if (editData?.id) {
      setForm({
        ...emptyForm,
        ...editData,
        // Other reward types have these null or 0 in the database; inputs need a value.
        freeSpinProvider: editData.freeSpinProvider || emptyForm.freeSpinProvider,
        freeSpinGameId: editData.freeSpinGameId || "",
        freeSpinGameName: editData.freeSpinGameName || "",
        freeSpinCount: editData.freeSpinCount || "",
        freeSpinBetUsd: editData.freeSpinBetUsd ?? "",
        freeSpinCoinLevel: editData.freeSpinCoinLevel ?? "",
        freeSpinCampaignId: editData.freeSpinCampaignId || "",
        freeSpinExpiryDays: editData.freeSpinExpiryDays || emptyForm.freeSpinExpiryDays,
        startDate: toPickerValue(editData.startDate),
        endDate: toPickerValue(editData.endDate),
      });
    } else {
      setForm(emptyForm);
      setBonusPackage(null);
    }
  }, [open, editData]);

  // Reconcile the selected package once packages are loaded (first id in the CSV).
  useEffect(() => {
    if (!editData?.bonusPackageIds || !packages.length) return;
    const firstId = String(editData.bonusPackageIds).split(",")[0].trim();
    setBonusPackage(packages.find((p) => String(p.id) === firstId) || null);
  }, [packages, editData]);

  const submit = () => {
    if (!form.promoCode.trim()) {
      ErrorToast("Promo code is required");
      return;
    }
    if (/\s/.test(form.promoCode)) {
      ErrorToast("Promo code cannot contain spaces");
      return;
    }
    if (!form.startDate) {
      ErrorToast("Start date is required");
      return;
    }
    if (!form.endDate) {
      ErrorToast("End date is required");
      return;
    }
    if (new Date(form.endDate) <= new Date(form.startDate)) {
      ErrorToast("End date must be after start date");
      return;
    }
    if (givesCash(form.rewardType) && !givesBonus(form.rewardType) && !(Number(form.cashRewardUsd) > 0)) {
      ErrorToast("Enter the cash reward amount in USD");
      return;
    }
    if (givesBonus(form.rewardType) && !givesCash(form.rewardType) && !(Number(form.bonusRewardUsd) > 0)) {
      ErrorToast("Enter the bonus reward amount in USD");
      return;
    }
    if (form.rewardType === "BOTH" && !(Number(form.cashRewardUsd) > 0) && !(Number(form.bonusRewardUsd) > 0)) {
      ErrorToast("Enter a cash or bonus amount in USD");
      return;
    }
    if (isBonusPercent(form.rewardType) && !(Number(form.bonusPercentage) > 0)) {
      ErrorToast("Enter the purchase bonus %");
      return;
    }
    if (isReferralBoost(form.rewardType) && !(Number(form.bonusPercentage) > 0)) {
      ErrorToast("Enter the referral boost %");
      return;
    }
    if (Number(form.minPurchaseAmount) > 0 && !(Number(form.purchasePeriodDays) > 0)) {
      ErrorToast("Purchase period must be at least 1 day");
      return;
    }
    if (isFreeSpins(form.rewardType)) {
      const netgaming = form.freeSpinProvider === "netgaming";
      if (!form.freeSpinGameId) {
        ErrorToast("Select a game for the free spins");
        return;
      }
      if (!(Number(form.freeSpinCount) > 0)) {
        ErrorToast("Enter the number of free spins");
        return;
      }
      if (netgaming && !form.freeSpinCampaignId.trim()) {
        ErrorToast("Campaign ID is required for NetGaming");
        return;
      }
      if (!netgaming && !(Number(form.freeSpinBetUsd) > 0)) {
        ErrorToast("Enter the USD stake for the free spins");
        return;
      }
    }
    const num = (v) => Number(v) || 0;
    const type = form.rewardType;
    const spins = isFreeSpins(type);
    const spinsNetgaming = spins && form.freeSpinProvider === "netgaming";
    const payload = {
      promoCode: form.promoCode.trim(),
      title: form.title,
      description: form.description,
      rewardType: type,
      // Both amounts are USD — the player is credited the equivalent in their own currency.
      cashRewardUsd: givesCash(type) ? num(form.cashRewardUsd) : 0,
      bonusRewardUsd: givesBonus(type) ? num(form.bonusRewardUsd) : 0,
      bonusPercentage: isBonusPercent(type) || isReferralBoost(type) ? num(form.bonusPercentage) : 0,
      bonusPackageIds: isBonusPercent(type) && bonusPackage ? String(bonusPackage.id) : null,
      // Purchase gate applies to every reward type. 0 = anyone may redeem.
      minPurchaseAmount: num(form.minPurchaseAmount),
      purchasePeriodDays: num(form.purchasePeriodDays) || 3,
      freeSpinProvider: spins ? form.freeSpinProvider : null,
      freeSpinGameId: spins ? form.freeSpinGameId : null,
      freeSpinGameName: spins ? form.freeSpinGameName : null,
      freeSpinCount: spins ? num(form.freeSpinCount) : 0,
      freeSpinBetUsd: spins && !spinsNetgaming ? num(form.freeSpinBetUsd) : null,
      freeSpinCoinLevel: spinsNetgaming && form.freeSpinCoinLevel !== "" ? num(form.freeSpinCoinLevel) : null,
      freeSpinCampaignId: spinsNetgaming ? form.freeSpinCampaignId.trim() : null,
      freeSpinExpiryDays: num(form.freeSpinExpiryDays) || 7,
      startDate: toInstant(form.startDate),
      endDate: toInstant(form.endDate),
      perUserLimit: num(form.perUserLimit),
      totalLimit: num(form.totalLimit),
      active: !!form.active,
    };
    if (editData?.id) payload.id = editData.id;

    const url = editData?.id ? endPoints.api.PROMO_CAMPAIGN_UPDATE : endPoints.api.PROMO_CAMPAIGN_ADD;
    setIsLoading(true);
    addData(url, payload)
      .then((res) => {
        setIsLoading(false);
        if (res?.data?.status === "success") {
          SuccesToast(res?.data?.message || "Promotion saved");
          onClose();
        } else {
          ErrorToast(res?.data?.message || "Failed to save promotion");
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
        ErrorToast("Failed to save promotion");
      });
  };

  const field = { width: "100%", marginTop: "16px" };
  const type = form.rewardType;

  return (
    <Dialog maxWidth="md" fullWidth open={open}>
      <DialogTitle sx={{ pr: 6 }}>
        {editData?.id ? "Edit" : "Create"} Promo Code
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8, color: "#64607a" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Toaster />
        {isLoading && <Loader spinner={true} visible={isLoading} />}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ marginTop: "8px" }}>
              Reward amounts are entered in <b>USD</b>. Each player is credited the equivalent
              in their own currency (fiat or crypto) at the exchange rate when they redeem.
            </Alert>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField sx={field} label="Promo Code *" variant="outlined" value={form.promoCode}
              helperText="No spaces allowed"
              onChange={(e) => setForm({ ...form, promoCode: e.target.value.toUpperCase().replace(/\s/g, "") })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} select label="Reward Type *" variant="outlined" value={type} onChange={set("rewardType")}>
              {REWARD_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} label="Title" variant="outlined" value={form.title} onChange={set("title")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} label="Description" variant="outlined" value={form.description} onChange={set("description")} />
          </Grid>

          {givesCash(type) && (
            <Grid item xs={12} sm={6}>
              <TextField sx={field} type="number" label="Cash Reward (USD)" variant="outlined"
                helperText="Paid into the withdrawable balance"
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                value={form.cashRewardUsd} onChange={set("cashRewardUsd")} />
            </Grid>
          )}
          {givesBonus(type) && (
            <Grid item xs={12} sm={6}>
              <TextField sx={field} type="number" label="Bonus Reward (USD)" variant="outlined"
                helperText="Paid into the bonus balance"
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                value={form.bonusRewardUsd} onChange={set("bonusRewardUsd")} />
            </Grid>
          )}

          {isBonusPercent(type) && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField sx={field} type="number" label="Bonus % on purchase" variant="outlined"
                  helperText="Credited to the bonus balance when a qualifying package is bought"
                  value={form.bonusPercentage} onChange={set("bonusPercentage")} />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  sx={field}
                  options={packages}
                  value={bonusPackage}
                  getOptionLabel={(p) => `${p?.package_name || "Package"} ($${p?.purchase_price})`}
                  isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                  onChange={(e, value) => setBonusPackage(value)}
                  renderInput={(params) => <TextField {...params} label="Bonus applies to this package (blank = any)" variant="outlined" />}
                />
              </Grid>
            </>
          )}

          {isReferralBoost(type) && (
            <Grid item xs={12} sm={6}>
              <TextField sx={field} type="number" label="Extra % on next referral bonus" variant="outlined"
                inputProps={{ min: 1, max: 500 }}
                helperText="Applied once, to the next referral that qualifies — not per referral"
                value={form.bonusPercentage} onChange={set("bonusPercentage")} />
            </Grid>
          )}

          {isFreeSpins(type) && (
            <>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ marginTop: "16px" }}>
                  {form.freeSpinProvider === "netgaming"
                    ? "NetGaming uses a coin level, which is the same for every currency."
                    : form.freeSpinProvider === "evoplay"
                    ? "Each player's bet is this USD stake in their currency, rounded down to a whole number. Players whose stake rounds down to 0, such as crypto wallets, cannot redeem this code."
                    : "Each player gets the highest bet the game allows at or below this USD stake in their currency, or the game's minimum bet if the stake is lower."}
                </Alert>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField sx={field} select label="Provider *" variant="outlined" value={form.freeSpinProvider}
                  onChange={(e) => setForm({
                    ...form,
                    freeSpinProvider: e.target.value,
                    freeSpinGameId: "",
                    freeSpinGameName: "",
                    freeSpinCount: "",
                  })}>
                  {FREE_SPIN_PROVIDERS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  sx={field}
                  options={spinGames}
                  value={form.freeSpinGameId ? { game_id: form.freeSpinGameId, game_name: form.freeSpinGameName } : null}
                  getOptionLabel={(g) => `${g?.game_name || "Game"} (${g?.game_id})`}
                  isOptionEqualToValue={(o, v) => String(o?.game_id) === String(v?.game_id)}
                  onChange={(e, g) => setForm({
                    ...form,
                    freeSpinGameId: g ? String(g.game_id) : "",
                    freeSpinGameName: g?.game_name || "",
                  })}
                  renderInput={(params) => <TextField {...params} label="Game *" variant="outlined" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {hasFixedSpinCounts(form.freeSpinProvider) ? (
                  <TextField sx={field} select label="Number of Spins *" variant="outlined"
                    value={form.freeSpinCount} onChange={set("freeSpinCount")}>
                    {FIXED_SPIN_COUNTS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                ) : (
                  <TextField sx={field} type="number" label="Number of Spins *" variant="outlined"
                    inputProps={{ min: 1 }} value={form.freeSpinCount} onChange={set("freeSpinCount")} />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField sx={field} type="number" label="Spins Valid For (days)" variant="outlined"
                  inputProps={{ min: 1 }} helperText="Counted from when the player redeems"
                  value={form.freeSpinExpiryDays} onChange={set("freeSpinExpiryDays")} />
              </Grid>
              {form.freeSpinProvider === "netgaming" ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField sx={field} label="Campaign ID *" variant="outlined" placeholder="cpmp12"
                      value={form.freeSpinCampaignId} onChange={set("freeSpinCampaignId")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField sx={field} type="number" label="Coin Value Level" variant="outlined"
                      placeholder="1" value={form.freeSpinCoinLevel} onChange={set("freeSpinCoinLevel")} />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} sm={6}>
                  <TextField sx={field} type="number" label="Stake per Spin (USD) *" variant="outlined"
                    inputProps={{ min: 0, step: "any" }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    value={form.freeSpinBetUsd} onChange={set("freeSpinBetUsd")} />
                </Grid>
              )}
            </>
          )}

          <Grid item xs={12}>
            <Alert severity={Number(form.minPurchaseAmount) > 0 ? "warning" : "info"} sx={{ marginTop: "16px" }}>
              {Number(form.minPurchaseAmount) > 0
                ? `Purchase required — only players who bought $${Number(form.minPurchaseAmount).toFixed(2)} or more in the last ${Number(form.purchasePeriodDays) || 3} day(s) can claim this code. The player sees the amount in their own currency.`
                : "No purchase requirement — any registered player can claim this code."}
            </Alert>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="number" label="Min Purchase Amount (USD)" variant="outlined"
              inputProps={{ min: 0, step: "0.01" }}
              helperText="Leave blank or 0 for no purchase requirement"
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              value={form.minPurchaseAmount} onChange={set("minPurchaseAmount")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="number" label="Purchase Period (days)" variant="outlined"
              inputProps={{ min: 1 }}
              helperText="How far back purchases are counted. Defaults to 3"
              disabled={!(Number(form.minPurchaseAmount) > 0)}
              value={form.purchasePeriodDays} onChange={set("purchasePeriodDays")} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="datetime-local" label="Start Date *" variant="outlined"
              InputLabelProps={{ shrink: true }} value={form.startDate} onChange={set("startDate")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="datetime-local" label="End Date *" variant="outlined"
              InputLabelProps={{ shrink: true }} value={form.endDate} onChange={set("endDate")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="number" label="Per-User Limit" variant="outlined"
              value={form.perUserLimit} onChange={set("perUserLimit")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="number" label="Total Limit (0 = unlimited)" variant="outlined"
              value={form.totalLimit} onChange={set("totalLimit")} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
              label="Active"
              sx={{ marginTop: "16px" }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit}>{editData?.id ? "Update" : "Create"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePromoCampaign;
